import { useLazyQuery } from '@apollo/client';
import {
	ProductType,
	StakingScreen_GetDailyStakingEnrollments,
	StakingScreen_GetDailyStakingEnrollmentsVariables,
} from '@app/codegen';
import { startOfDay } from 'date-fns';
import React, { useEffect } from 'react';

import { useStakingScreenForm } from './StakingForm';
import { STAKING_SCREEN_GET_DAILY_STAKING_ENROLLMENTS } from './StakingScreenGraphql';

export type StakingScreenState = ReturnType<typeof useStakingScreenState>;

export const StakingScreenContext = React.createContext<StakingScreenState | undefined>(undefined);

export type StakingScreenProviderProps = {
	value: StakingScreenState | undefined;
	children: React.ReactNode;
};

export function useStakingScreenState() {
	const stakingForm = useStakingScreenForm();
	const date = startOfDay(new Date()).toISOString();

	const [getDailyStakingEnrollments, getDailyStakingEnrollmentsQuery] = useLazyQuery<
		StakingScreen_GetDailyStakingEnrollments,
		StakingScreen_GetDailyStakingEnrollmentsVariables
	>(STAKING_SCREEN_GET_DAILY_STAKING_ENROLLMENTS, {
		fetchPolicy: 'cache-and-network',
	});

	useEffect(() => {
		if (date) {
			getDailyStakingEnrollments({
				variables: { input: { date } },
			});
		}
	}, [date, getDailyStakingEnrollments]);

	const stakingData = getDailyStakingEnrollmentsQuery.data?.dailyStakingEnrollments?.data;

	const refetchStakingData = () => {
		if (date) {
			getDailyStakingEnrollments({
				variables: { input: { date } },
			});
		}
	};

	return {
		stakingBalances: stakingData?.stakingBalances,
		iraStakingAmounts: stakingData?.stakingAmounts?.find(
			(x) => x.productType === ProductType.IRA
		)?.stakeStakingAmounts,
		pcaStakingAmounts: stakingData?.stakingAmounts?.find(
			(x) => x.productType === ProductType.PCA
		)?.stakeStakingAmounts,
		tpaStakingAmounts: stakingData?.stakingAmounts?.find(
			(x) => x.productType === ProductType.TPA
		)?.stakeStakingAmounts,
		iraUnstakingAmounts: stakingData?.stakingAmounts?.find(
			(x) => x.productType === ProductType.IRA
		)?.unstakeStakingAmounts,
		pcaUnstakingAmounts: stakingData?.stakingAmounts?.find(
			(x) => x.productType === ProductType.PCA
		)?.unstakeStakingAmounts,
		tpaUnstakingAmounts: stakingData?.stakingAmounts?.find(
			(x) => x.productType === ProductType.TPA
		)?.unstakeStakingAmounts,
		stakingForm,
		loading: getDailyStakingEnrollmentsQuery.loading,
		refetchStakingData,
		getDailyStakingEnrollments,
	};
}

export function StakingScreenProvider(props: StakingScreenProviderProps | null) {
	return (
		<StakingScreenContext.Provider value={props?.value}>
			{props?.children}
		</StakingScreenContext.Provider>
	);
}

export function useStakingScreenContext() {
	const context = React.useContext(StakingScreenContext);

	if (!context)
		throw new Error('useStakingScreenContext must be used within a StakingScreenProvider');

	return context;
}
